// ============================================================
// TMR Portal Admin — as versões que um modelo já teve, e o que cada uma mudou
// ============================================================
import { useState } from 'react';
import { Btn, Segmented } from '../../components/primitives';
import { ErrorState, Skeleton } from '../../components/states';
import { useRenderedVersion, useTemplateDiff } from '../../data/contractTemplates/queries';
import {
  previousPublished,
  publishedHistory,
  shortHash,
  versionStatusLabel,
} from '../../data/contractTemplates/status';
import type { ContractTemplateDetail, TemplateVersion } from '../../data/contractTemplates/contract';
import { TemplateDiffView } from './TemplateDiffView';

/** Props for {@link TemplateHistory}. */
interface TemplateHistoryProps {
  template: ContractTemplateDetail;
}

/**
 * Every version the template has had in force, and what each one changed.
 *
 * **This is where "why does this contract say that" is answered.** A signed contract points at the version
 * it was issued from, and months later somebody asks what that version said and when the clause changed.
 * Each version opens on what it changed against the one before — the question usually asked — and on its
 * text rendered with the type's example values, which is how it read to the patient.
 *
 * One version open at a time: two open comparisons, one under the other, read as a single long diff.
 */
export function TemplateHistory({ template }: TemplateHistoryProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const history = publishedHistory(template.versions);

  return (
    <div className="pa-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--fg-subtle)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        Histórico de versões
      </span>

      {history.length === 0 ? (
        <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
          {template.companyId === null
            ? 'Nenhuma versão publicada ainda. Enquanto isso, nenhum contrato deste tipo é emitido por este modelo.'
            : 'Nenhuma versão publicada ainda. Enquanto isso, a clínica assina pelo modelo global.'}
        </span>
      ) : (
        history.map((version) => (
          <VersionEntry
            key={version.id}
            template={template}
            version={version}
            previous={previousPublished(template.versions, version)}
            open={openId === version.id}
            onToggle={() => setOpenId((current) => (current === version.id ? null : version.id))}
          />
        ))
      )}
    </div>
  );
}

/** One version: who put it in force and why, and — when opened — what it changed and how it read. */
function VersionEntry({
  template,
  version,
  previous,
  open,
  onToggle,
}: {
  template: ContractTemplateDetail;
  version: TemplateVersion;
  previous: TemplateVersion | null;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 10, borderBottom: '1px solid var(--border-default)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Versão {version.number} · {versionStatusLabel(version.status)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
            Publicada {version.publishedAt ? `em ${day(version.publishedAt)} ` : ''}por {version.publishedByName ?? '—'}
          </span>
          {version.note && (
            <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{version.note}</span>
          )}
        </div>
        <Btn variant="secondary" size="sm" onClick={onToggle}>
          {open ? 'Fechar' : 'Ver'}
        </Btn>
      </div>

      {open && <VersionDetail template={template} version={version} previous={previous} />}
    </div>
  );
}

/** The opened version: what it changed, or its text as the patient read it. */
function VersionDetail({
  template,
  version,
  previous,
}: {
  template: ContractTemplateDetail;
  version: TemplateVersion;
  previous: TemplateVersion | null;
}) {
  // The first publication changed nothing — there was nothing before it — so it opens on its text.
  const [tab, setTab] = useState<'changes' | 'text'>(previous ? 'changes' : 'text');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <Segmented<'changes' | 'text'>
          size="sm"
          value={tab}
          onChange={setTab}
          options={[
            ...(previous ? [{ value: 'changes' as const, label: 'O que mudou' }] : []),
            { value: 'text' as const, label: 'Texto com dados de exemplo' },
          ]}
        />
      </div>

      {tab === 'changes' && previous ? (
        <Changes template={template} version={version} previous={previous} />
      ) : (
        <RenderedText template={template} version={version} />
      )}

      <span
        title="SHA-256 do texto publicado"
        style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}
      >
        SHA-256 {shortHash(version.contentHash)}
      </span>
    </div>
  );
}

function Changes({
  template,
  version,
  previous,
}: {
  template: ContractTemplateDetail;
  version: TemplateVersion;
  previous: TemplateVersion;
}) {
  const diff = useTemplateDiff(template.id, version.id, previous.id);

  if (diff.isPending) return <Skeleton rows={4} />;
  if (diff.isError) return <ErrorState error={diff.error} onRetry={() => void diff.refetch()} />;

  return <TemplateDiffView diff={diff.data} />;
}

function RenderedText({ template, version }: { template: ContractTemplateDetail; version: TemplateVersion }) {
  const rendered = useRenderedVersion(template.id, version, template.documentType);

  if (rendered.isPending) return <Skeleton rows={4} />;
  if (rendered.isError) return <ErrorState error={rendered.error} onRetry={() => void rendered.refetch()} />;

  return (
    // Sanitized by the backend, where the Markdown is parsed — the same rendering the editor's preview uses.
    <div
      className="pa-prose"
      style={{
        fontSize: 13.5,
        lineHeight: 1.7,
        maxHeight: 420,
        overflowY: 'auto',
        padding: 12,
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-sm)',
      }}
      dangerouslySetInnerHTML={{ __html: rendered.data.html }}
    />
  );
}

/** A day in the operator's timezone. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}
